import {
  createBrowserRouter,
  Navigate,
  type RouterProviderProps,
  type RouteObject,
} from 'react-router-dom';
import { LoginPage } from '@/pages/LoginPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { KalenderAbbestellenPage } from '@/pages/KalenderAbbestellenPage';
import EinladungPage from '@/pages/EinladungPage';
import { TreePage } from '@/pages/TreePage';
import { SearchPage } from '@/pages/SearchPage';
import { SecuritySettingsPage } from '@/pages/SecuritySettingsPage';
import { VorschlagPage } from '@/pages/VorschlagPage';
import { StatsPage } from '@/pages/StatsPage';
import { MedicalStatsPage } from '@/pages/MedicalStatsPage';
import { InheritancePage } from '@/pages/InheritancePage';
import { MedicalCatalogPage } from '@/pages/MedicalCatalogPage';
import { RelationshipFinderPage } from '@/pages/RelationshipFinderPage';
import { LifelinePage } from '@/pages/LifelinePage';
import { QuizPage } from '@/pages/QuizPage';
import { CalendarPage } from '@/pages/CalendarPage';
import AchievementsPage from '@/pages/AchievementsPage';
import ColdCasesPage from '@/pages/ColdCasesPage';
import { FogOfWarPage } from '@/pages/FogOfWarPage';
import { CosmosPage } from '@/pages/CosmosPage';
import { FanChartPage } from '@/pages/FanChartPage';
import { ArchivePage } from '@/pages/ArchivePage';
import { SourceCoveragePage } from '@/pages/SourceCoveragePage';
import { PlausibilityPage } from '@/pages/PlausibilityPage';
import { OrtePage } from '@/pages/OrtePage';
import { MembersPage } from '@/pages/MembersPage';
import { TreeSettingsPage } from '@/pages/TreeSettingsPage';
import { VerwaltungUmgezogenPage } from '@/pages/VerwaltungUmgezogenPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { NoAccessPage } from '@/pages/NoAccessPage';
import { AuthGuard } from '@/components/Auth/AuthGuard';
import { TreeGuard } from '@/components/Auth/TreeGuard';
import { RequirePermission } from '@/components/Auth/RequirePermission';
import { RequireFeature } from '@/components/Auth/RequireFeature';
import { RouteErrorBoundary } from '@/components/common/ErrorBoundary';

const routes: RouteObject[] = [
  // ─── Öffentliche Routen ───────────────────────────────────────────────
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
  },
  {
    // Aus der Erinnerungsmail – ohne Anmeldung.
    path: '/kalender/abbestellen/:token',
    element: <KalenderAbbestellenPage />,
  },

  // ─── Geschützte Routen (Auth erforderlich) ────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      {
        path: '/no-access',
        element: <NoAccessPage />,
      },
      {
        // Unter dem AuthGuard, damit ein Reload der Seite per Silent-Refresh
        // wieder ein gültiges Access-/CSRF-Token erhält. Erzwungene
        // Passwortänderungen (mustChangePassword) leitet der AuthGuard
        // ohnehin hierher.
        path: '/change-password',
        element: <ChangePasswordPage />,
      },
      {
        path: '/settings/security',
        element: <SecuritySettingsPage />,
      },
      {
        // Ohne TreeGuard: der Draht zum Betreiber gilt dem Konto. Wer noch
        // keinen Stammbaum hat, soll trotzdem einen Fehler melden können –
        // vielleicht ist das ja der Fehler.
        path: '/vorschlag',
        element: <VorschlagPage />,
      },

      {
        // Ohne TreeGuard, und das ist der Punkt: wer seiner ERSTEN Einladung
        // folgt, hat noch keinen Stammbaum. Hinter dem Guard stünde er vor
        // „Kein Stammbaum ausgewählt" statt vor der Frage, ob er beitreten
        // möchte.
        path: '/einladung',
        element: <EinladungPage />,
      },

      // ─── Datenführende Routen: erst Stammbaum sicherstellen ──────────
      {
        element: <TreeGuard />,
        children: [
      {
        element: <RequirePermission permission="persons.view" />,
        children: [{ path: '/', element: <TreePage /> }],
      },
      {
        element: <RequirePermission permission="search.view" />,
        children: [{ path: '/search', element: <SearchPage /> }],
      },
      {
        element: <RequirePermission permission="statistics.view" />,
        children: [
          {
            element: <RequireFeature feature="statistics" />,
            children: [{ path: '/statistics', element: <StatsPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="medical.view" sensitive />,
        children: [
          {
            element: <RequireFeature feature="medical" />,
            children: [{ path: '/medical-stats', element: <MedicalStatsPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="medical.view" sensitive />,
        children: [
          {
            element: <RequireFeature feature="medical" />,
            children: [{ path: '/inheritance', element: <InheritancePage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="medical.view" sensitive />,
        children: [
          {
            element: <RequireFeature feature="medical" />,
            children: [{ path: '/medical-catalog', element: <MedicalCatalogPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="relationshipFinder.view" />,
        children: [
          {
            element: <RequireFeature feature="relationshipFinder" />,
            children: [{ path: '/relationship-finder', element: <RelationshipFinderPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="lifelines.view" />,
        children: [
          {
            element: <RequireFeature feature="lifelines" />,
            children: [{ path: '/lifelines', element: <LifelinePage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="quiz.view" />,
        children: [
          {
            element: <RequireFeature feature="quiz" />,
            children: [{ path: '/quiz', element: <QuizPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="calendar.view" />,
        children: [
          {
            element: <RequireFeature feature="calendar" />,
            children: [{ path: '/calendar', element: <CalendarPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="achievements.view" />,
        children: [
          {
            element: <RequireFeature feature="achievements" />,
            children: [{ path: '/achievements', element: <AchievementsPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="coldCases.view" />,
        children: [
          {
            element: <RequireFeature feature="coldCases" />,
            children: [{ path: '/cold-cases', element: <ColdCasesPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="fogOfWar.view" />,
        children: [
          {
            element: <RequireFeature feature="fogOfWar" />,
            children: [{ path: '/fog-of-war', element: <FogOfWarPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="cosmos.view" />,
        children: [
          {
            element: <RequireFeature feature="cosmos" />,
            children: [{ path: '/cosmos', element: <CosmosPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="persons.view" />,
        children: [
          {
            element: <RequireFeature feature="fanChart" />,
            children: [{ path: '/fan-chart', element: <FanChartPage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="archive.view" />,
        children: [
          {
            element: <RequireFeature feature="archive" />,
            children: [{ path: '/archive', element: <ArchivePage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="persons.view" />,
        children: [
          {
            element: <RequireFeature feature="sourceCoverage" />,
            children: [{ path: '/sources', element: <SourceCoveragePage /> }],
          },
        ],
      },
      {
        element: <RequirePermission permission="persons.view" />,
        children: [
          {
            element: <RequireFeature feature="plausibility" />,
            children: [{ path: '/plausibility', element: <PlausibilityPage /> }],
          },
          // Orte hängen an keinem Tarif – ohne sie stimmt die Karte nicht, und
          // die Karte gibt es überall, wo es Personen gibt.
          { path: '/orte', element: <OrtePage /> },
        ],
      },
      {
        // Mitglieder des aktiven Stammbaums – Sache des Besitzers. Bewusst
        // ohne RequirePermission: Besitz ist kein Recht aus einer Rolle,
        // sondern eine Stellung. Wer nicht Besitzer ist, bekommt vom Server
        // eine 403 und von der Seite eine Erklärung.
        path: '/members',
        element: <MembersPage />,
      },
      {
        // Der Stammbaum als Ganzes – ebenfalls Sache des Besitzers.
        path: '/tree-settings',
        element: <TreeSettingsPage />,
      },
      {
        // Zwei Umzüge hintereinander: die Stammbaum-Sicherung war erst in der
        // Verwaltung, die Verwaltung ist inzwischen ganz aus der Anwendung
        // heraus. Alte Lesezeichen sollen das erfahren, nicht ins Leere
        // laufen.
        path: '/tree-backup',
        element: <Navigate to="/admin" replace />,
      },

      // ─── Ehemalige Verwaltung ─────────────────────────────────────────
      //
      // Alles darunter liegt jetzt in der Betreiberkonsole (eigene
      // Anwendung, eigene Anmeldung). Hier steht nur noch der Hinweis –
      // bewusst ohne RequireAdmin: wer ein altes Lesezeichen hat, soll die
      // Erklärung sehen und nicht auf eine Rechte-Wand laufen.
      {
        path: '/admin/*',
        element: <VerwaltungUmgezogenPage />,
      },
      {
        path: '/admin',
        element: <VerwaltungUmgezogenPage />,
      },
        ],
      },
    ],
  },

  // ─── 404 ──────────────────────────────────────────────────────────────
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export const router: RouterProviderProps['router'] = createBrowserRouter([
  {
    // Auffangnetz: Render-/Loader-Fehler in einer Route zeigen die
    // ErrorBoundary statt eines weißen Bildschirms.
    errorElement: <RouteErrorBoundary />,
    children: routes,
  },
]);
